// 封装一个上传日志的函数
import { uploadLog } from "@saas/server-api/src/log/index";
import store from "@saas/store/src";
import { parseTime } from "@saas/components/src/utils/utils";

// 日志上传策略配置
const LOG_CONFIG = {
  BATCH_SIZE: 50, // 累积50条日志触发上传
  UPLOAD_INTERVAL: 3 * 60 * 1000, // 3分钟触发上传
  MAX_RETRY: 3, // 最大重试次数
  MAX_QUEUE_SIZE: 1000, // 队列最大长度，防止内存溢出
  MAX_UPLOAD_COUNT: 100, // 单次最多上传100条，防止接口超时
};
let version = window.appVersion || "unknown";
// 日志队列
let logQueue = [];
// 上传定时器
let uploadTimer = null;
// 上传锁，防止并发上传
let isUploading = false;
// 失败重试计数（使用日志ID作为key，防止内存泄漏）
const retryCountMap = new Map(); // key: logId, value: retryCount
// 日志ID计数器
let logIdCounter = 0;
// 连续上传计数器（防止无限递归）
let consecutiveFlushCount = 0;
const MAX_CONSECUTIVE_FLUSH = 10;

/**
 * 启动上传定时器
 */
const startUploadTimer = () => {
  if (uploadTimer) {
    return;
  }
  uploadTimer = setTimeout(() => {
    flushLogs();
  }, LOG_CONFIG.UPLOAD_INTERVAL);
};

/**
 * 清除上传定时器
 */
const clearUploadTimer = () => {
  if (uploadTimer) {
    clearTimeout(uploadTimer);
    uploadTimer = null;
  }
};

/**
 * 批量上传日志（加锁防止并发）
 */
const flushLogs = async () => {
  // 如果队列为空，直接返回
  if (logQueue.length === 0) {
    return;
  }

  // 如果正在上传，直接返回（防止并发）
  if (isUploading) {
    console.log(
      `⚠️ 正在上传日志，跳过本次上传（当前队列: ${logQueue.length} 条）`
    );

    // ⭐ 关键修复：确保队列中的日志最终会被上传
    // 如果队列有日志且没有定时器，启动定时器
    if (logQueue.length > 0 && !uploadTimer) {
      console.log("🔄 为跳过的日志启动定时器");
      startUploadTimer();
    }

    return;
  }

  // 加锁
  isUploading = true;

  // 从队列中取出最多 MAX_UPLOAD_COUNT 条日志（防止一次上传太多导致接口超时）
  const uploadCount = Math.min(logQueue.length, LOG_CONFIG.MAX_UPLOAD_COUNT);
  const logsToUpload = logQueue.splice(0, uploadCount); // 从队列头部取出指定数量，剩余保留在队列中
  clearUploadTimer();

  try {
    // 移除内部字段，只上传业务数据
    const logsToSend = logsToUpload.map((log) => {
      const { _logId, ...rest } = log;
      return rest;
    });

    // 批量上传所有日志
    await uploadLog(logsToSend);
    console.log(`✅ 成功上传 ${logsToUpload.length} 条日志`);

    // 上传成功，重置连续上传计数
    consecutiveFlushCount = 0;

    // 上传成功，清除重试计数
    logsToUpload.forEach((log) => {
      if (log._logId) {
        retryCountMap.delete(log._logId);
      }
    });
  } catch (error) {
    console.error("❌ 批量上传日志失败:", error);

    // 过滤出可以重试的日志（未超过最大重试次数）
    const logsToRetry = logsToUpload.filter((log) => {
      const logId = log._logId;
      if (!logId) return false; // 没有ID的日志不重试

      const retryCount = retryCountMap.get(logId) || 0;
      if (retryCount < LOG_CONFIG.MAX_RETRY) {
        retryCountMap.set(logId, retryCount + 1);
        return true;
      } else {
        console.warn("日志重试次数超限，丢弃:", log.message);
        retryCountMap.delete(logId);
        return false;
      }
    });

    // 重新加入队列（加到队列前面，优先上传）
    if (logsToRetry.length > 0) {
      logQueue.unshift(...logsToRetry);
      console.log(`重新加入队列: ${logsToRetry.length} 条日志`);

      // 重启定时器，稍后重试
      startUploadTimer();
    }
  } finally {
    // 释放锁
    isUploading = false;

    // ⭐ 关键修复：上传完成后，如果队列还有日志，确保会被上传
    if (logQueue.length > 0) {
      console.log(`📋 上传完成，队列还有 ${logQueue.length} 条日志`);

      // 如果队列还有足够多的日志（达到批量上传阈值或达到单次上传上限），立即再次上传
      // 这样可以确保大量日志能够快速分批上传完成
      if (
        logQueue.length >= LOG_CONFIG.BATCH_SIZE ||
        logQueue.length >= LOG_CONFIG.MAX_UPLOAD_COUNT
      ) {
        // ⭐ 防止无限递归：检查连续上传次数
        consecutiveFlushCount++;

        if (consecutiveFlushCount <= MAX_CONSECUTIVE_FLUSH) {
          console.log(
            `🔄 队列还有 ${logQueue.length} 条日志，立即触发下一次上传 (${consecutiveFlushCount}/${MAX_CONSECUTIVE_FLUSH})`
          );
          // 异步调用，避免递归阻塞
          setTimeout(() => flushLogs(), 0);
        } else {
          console.error(
            `❌ 连续上传次数过多 (${consecutiveFlushCount})，停止自动上传，启动定时器`
          );
          consecutiveFlushCount = 0; // 重置计数
          startUploadTimer(); // 改用定时器
        }
      } else if (!uploadTimer) {
        // 否则启动定时器
        console.log("🔄 为剩余日志启动定时器");
        startUploadTimer();
      }
    } else {
      // 队列为空，重置连续上传计数
      consecutiveFlushCount = 0;
    }

    // 定期清理过期的重试计数
    cleanupRetryCountMap();
  }
};

// 获取网络类型
const getNetworkType = () => {
  try {
    return navigator.connection?.effectiveType || "unknown";
  } catch (error) {
    return "unknown";
  }
};
// 获取屏幕分辨率
const getScreenResolution = () => {
  try {
    return `${window.innerWidth || 0}x${window.innerHeight || 0}`;
  } catch (error) {
    return "0x0";
  }
};
/**
 * 添加日志到队列（直播间日志）
 * @param {Object} params - 日志参数
 * @param {Object} params.tag - 日志标签  userAction
 * @param {string} params.level - 日志级别 info error
 * @param {string} params.module - 模块
 * @param {string} params.message - 日志消息
 */
export const uploadLiveLog = async (params) => {
  try {
    console.log("uploadLiveLog====", params);
    const {
      message = "",
      module = "live_room",
      level = "info",
      tag = "userAction",
      extra = {},
    } = params;

    // 版本号获取优化：只获取一次
    if (!version) {
      version = window.appVersion || "unknown";
    }

    const userInfo = store.getters.user || {};
    const roomId = store.state.live?.roomInfo?.id || "";
    const isElectron = window.isElectron;

    const logData = {
      _logId: ++logIdCounter, // 唯一ID，用于重试计数
      timestamp: parseTime(Date.now()),
      level: level,
      module: module, // 模块,比如直播，白板，app
      message: message,
      product_name: "魔果云课", // 用来区分应用
      tag: tag, // 日志标签
      base_info: {
        app_version: version,
        device_model: navigator.userAgent,
        os_name: isElectron ? "electron" : "web", // app端
        screen_resolution: getScreenResolution(),
        network_type: getNetworkType(),
        language: navigator.language,
      },
      // 自定义
      extra: {
        currentUserId: userInfo.user_id,
        roomId: roomId,
        ...JSON.parse(JSON.stringify(extra)),
      },
    };

    // 检查队列大小，防止内存溢出
    if (logQueue.length >= LOG_CONFIG.MAX_QUEUE_SIZE) {
      console.warn(
        `⚠️ 日志队列已满 (${logQueue.length}/${LOG_CONFIG.MAX_QUEUE_SIZE})，丢弃最旧的日志`
      );
      // 移除最旧的日志（队列头部）
      const removed = logQueue.shift();
      if (removed && removed._logId) {
        retryCountMap.delete(removed._logId);
      }
    }

    // 加入队列
    logQueue.push(logData);

    // 检查是否需要立即上传
    if (logQueue.length >= LOG_CONFIG.BATCH_SIZE) {
      // 不 await，避免阻塞
      flushLogs().catch((err) => {
        console.error("flushLogs 调用失败:", err);
      });
    } else {
      // 启动定时器（如果还没启动）
      startUploadTimer();
    }
  } catch (error) {
    console.error("添加日志到队列失败:", error);
  }
};

/**
 * 手动触发上传所有待上传的日志
 */
export const forceFlushLogs = () => {
  return new Promise((resolve, reject) => {
    flushLogs()
      .then(() => {
        resolve();
      })
      .catch((error) => {
        console.error("forceFlushLogs error", error);
        reject(error);
      });
  });
};

/**
 * 清空日志队列（用于特殊场景，如用户退出直播间）
 */
export const clearLogQueue = () => {
  // 清空队列前，清理对应的重试计数
  logQueue.forEach((log) => {
    if (log._logId) {
      retryCountMap.delete(log._logId);
    }
  });

  logQueue = [];
  clearUploadTimer();

  // 清理所有重试计数（防止内存泄漏）
  retryCountMap.clear();

  // 重置连续上传计数
  consecutiveFlushCount = 0;

  console.log("🗑️ 日志队列已清空");
};

/**
 * 定期清理过期的重试计数（防止 Map 无限增长）
 */
const cleanupRetryCountMap = () => {
  if (retryCountMap.size > 100) {
    console.warn(`⚠️ retryCountMap 过大 (${retryCountMap.size})，清理中...`);

    // 获取当前队列中的所有 logId
    const activeLogIds = new Set(logQueue.map((log) => log._logId));

    // 删除不在队列中的计数
    const keysToDelete = [];
    retryCountMap.forEach((count, logId) => {
      if (!activeLogIds.has(logId)) {
        keysToDelete.push(logId);
      }
    });

    keysToDelete.forEach((key) => retryCountMap.delete(key));
    console.log(`🗑️ 清理了 ${keysToDelete.length} 个过期的重试计数`);
  }
};
